import { datetime, type DatetimeConfig } from './datetime-config';

interface DateRangeParams {
  startDate: DatetimeConfig;
  endDate: DatetimeConfig;
  targetDate?: DatetimeConfig;
  includeStart?: boolean;
  includeEnd?: boolean;
}

/**
 * 주어진 날짜가 특정 기간 내에 있는지 확인합니다
 *
 * @param params.startDate - 시작 날짜
 * @param params.endDate - 종료 날짜
 * @param params.targetDate - 확인할 날짜 (기본값: 오늘)
 * @param params.includeStart - 시작일 포함 여부 (기본값: true)
 * @param params.includeEnd - 종료일 포함 여부 (기본값: true)
 * @returns {boolean} 기간 내 포함 여부
 * @example
 * ```typescript
 * // 오늘이 특정 기간 내에 있는지
 * isDateInRange({ startDate: '2024-03-01', endDate: '2024-03-31' });
 *
 * // 특정 날짜가 기간 내에 있는지
 * isDateInRange({
 *   startDate: '2024-03-01',
 *   endDate: '2024-03-31',
 *   targetDate: '2024-03-15'
 * });
 * ```
 */
export const isDateInRange = ({
  startDate,
  endDate,
  targetDate = datetime().format('YYYY-MM-DD:HH:mm:ss'),
  includeStart,
  includeEnd,
}: DateRangeParams): boolean => {
  const target = datetime(targetDate);
  const start = datetime(startDate);
  const end = datetime(endDate);

  const isAfterStart = includeStart ? target.isSameOrAfter(start) : target.isAfter(start);

  const isBeforeEnd = includeEnd ? target.isSameOrBefore(end) : target.isBefore(end);

  return isAfterStart && isBeforeEnd;
};
